/**
 * Painel de TARPs FECHADAS (GUPM-188) — auditoria rápida no rodapé do M3.
 * Colapsável (fechado por padrão, como o V8), + filtros (tipo de TARP, barragem)
 * e ordenação (mais recente | maior severidade) — vai além do V8, a pedido do dono.
 * Read-only: clicar abre o detalhe (baixa registrada) via TarpDetailModal. A
 * severidade usa o NÍVEL EFETIVO (GUPM-207).
 */
import { useMemo, useState } from 'react';
import { LEVEL_UI } from '@/ui';
import { filtrarOrdenarFechadas, nivelEfetivo, TARP_CODES, type OrdenacaoFechadas } from '@/domain';
import { agruparAutoriaPorTarp, autoriaParaExibicao } from '@/domain/tarp/autoria';
import { rotuloDaEstrutura } from '@/domain/estrutura/rotulo';
import type { AutoriaTarp, TarpRecord } from '@/services/datasources';
import { tarpCodeLabel, tarpDisplayId } from '../catalog-stub';

const SELECT_CLS = 'rounded-lg border border-jf-border bg-jf-surface2 px-2 py-1 text-xs text-white';

export function TarpsFechadasPanel({
  tarps,
  onSelect,
  autoria,
}: {
  tarps: TarpRecord[];
  onSelect: (t: TarpRecord) => void;
  /**
   * GUPM-301: atribuições de autoria (lote). Aqui interessa só o momento `baixa` —
   * numa auditoria ANM, quem encerrou é a assinatura do registro. Ausente/vazio =
   * consulta não carregou ou falhou: o rótulo é omitido, a linha aparece igual.
   *
   * `iniciou`/`editou`/`abriu` de uma TARP **encerrada** não aparecem em nenhuma tela
   * curada hoje — nem no `TarpDetailModal` (que não recebe `autoria`) nem no M10 (feed
   * de leituras/eventos; o audit trail migrou para `/auditoria` no GUPM-138). Só o
   * `TarpList`, que recebe exclusivamente TARPs abertas, monta a trilha completa. Dá
   * para reconstruir em `/auditoria`, mas é log cru e restrito a RT/admin.
   * Fora do escopo deste card — o card pede "Baixa por". Registrado aqui para o
   * próximo leitor não sair procurando a trilha onde ela não está.
   */
  autoria?: readonly AutoriaTarp[];
}) {
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState('todos');
  const [damCode, setDamCode] = useState('todas');
  const [ordenar, setOrdenar] = useState<OrdenacaoFechadas>('recente');

  const damCodes = useMemo(
    () => [...new Set(tarps.map((t) => t.damCode).filter(Boolean))].sort(),
    [tarps],
  );
  const lista = useMemo(
    () => filtrarOrdenarFechadas(tarps, { tipo, damCode, ordenar }),
    [tarps, tipo, damCode, ordenar],
  );
  // GUPM-301: indexa a autoria uma vez, não uma varredura por linha.
  const autoriaPorTarp = useMemo(() => agruparAutoriaPorTarp(autoria ?? []), [autoria]);

  // Sem fechadas → seção some (não polui a tela).
  if (tarps.length === 0) return null;

  return (
    <section className="rounded-xl border border-jf-border bg-jf-surface">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition hover:bg-white/[0.03]"
      >
        <span className="text-sm font-bold text-jf-muted">
          ✅ Auditoria — {tarps.length} TARP{tarps.length > 1 ? 's' : ''} baixada{tarps.length > 1 ? 's' : ''}
        </span>
        <span className="text-xs text-jf-dim">{open ? 'ocultar ▴' : 'mostrar ▾'}</span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-jf-border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <select aria-label="Filtrar por tipo de TARP" className={SELECT_CLS} value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="todos">Todos os tipos</option>
              {TARP_CODES.map((c) => (
                <option key={c} value={c}>{tarpCodeLabel(c)}</option>
              ))}
            </select>
            {/* "estrutura", e não "barragem": desde o GUPM-329 `dams` tem a Mina, que
                não é barragem regulada — e o rótulo vem do helper, não de um
                rotulo montado a mao aqui. */}
            <select aria-label="Filtrar por estrutura" className={SELECT_CLS} value={damCode} onChange={(e) => setDamCode(e.target.value)}>
              <option value="todas">Todas as estruturas</option>
              {damCodes.map((c) => (
                <option key={c} value={c}>{rotuloDaEstrutura(c) ?? c}</option>
              ))}
            </select>
            <select aria-label="Ordenar" className={SELECT_CLS} value={ordenar} onChange={(e) => setOrdenar(e.target.value as OrdenacaoFechadas)}>
              <option value="recente">Mais recentes primeiro</option>
              <option value="severidade">Maior severidade primeiro</option>
            </select>
          </div>

          {lista.length === 0 ? (
            <p className="py-2 text-center text-xs text-jf-muted">Nenhuma TARP baixada com esses filtros.</p>
          ) : (
            <ul className="space-y-2">
              {lista.map((t) => {
                const u = LEVEL_UI[nivelEfetivo(t.tarpCode, t.flowState)];
                const id = tarpDisplayId(t.seq, t.damCode, t.tarpCode, t.instrumentCode);
                // Reusa o domínio para o rótulo (não duplica texto nem regra) e mantém
                // só o momento `baixa` — momento sem entrada já sai omitido de lá.
                const baixaPor = autoriaParaExibicao(autoriaPorTarp[t.id] ?? []).find(
                  (a) => a.momento === 'baixa',
                );
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(t)}
                      className="block w-full rounded-lg border px-3 py-2 text-left transition hover:brightness-110"
                      style={{ borderColor: u.border, background: u.bg }}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-sm font-bold text-white" title={id.full}>{id.num}</span>
                        {t.damCode ? (
                          <span className="rounded-full border border-jf-border bg-jf-surface2 px-2 py-0.5 text-[11px] font-bold text-slate-200">
                            {rotuloDaEstrutura(t.damCode) ?? t.damCode}
                          </span>
                        ) : (
                          <span className="text-[11px] text-jf-muted">Ainda não identificada</span>
                        )}
                        <span className="text-[11px] font-bold" style={{ color: u.color }}>{u.label}</span>
                        <span className="ml-auto text-[11px] text-jf-dim">
                          {t.closedAt ? `baixada ${new Date(t.closedAt).toLocaleDateString('pt-BR')}` : 'baixada'}
                        </span>
                      </div>
                      <div className="mt-0.5 text-sm text-white">
                        <span aria-hidden className="mr-1">{u.icon}</span>
                        {tarpCodeLabel(t.tarpCode, t.instrumentType)}
                      </div>
                      {baixaPor && (
                        <div className="mt-0.5 text-xs text-jf-muted">
                          {baixaPor.label}: {baixaPor.nomes}
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
